import { useMutation, useQueryClient } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"
import { TrialBlock } from "@/types/trialBlock"
import { getSupabaseFunctionErrorMessage } from "@/utils/supabaseFunctionUtils"

export interface ReviewFormData extends Record<string, unknown> {
  result?: string // replaces reviewStatus and trialShiftResult
  comments?: string
  attended?: boolean
  reviewed_at?: string
  // Delivery protocol scores
  deliveryPictures?: string
  deliveryNotes?: string
  instructionCompliance?: string
  deliverySuccessScore?: number // Note: this is a bigint in the DB
  // Road safety scores
  drivingBehaviour?: string
  vehicleHandling?: string
  safetyScore?: number // Note: this is a bigint in the DB
  // Customer interaction scores
  customerFeedback?: string
  issueResolution?: string
  // Additional fields from schema
  timeManagement?: string
  loadCheck?: string
  vehicleName?: string
  additionalTrial?: boolean
}

export const useUpdateTrialBlock = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, formData }: { id: string; formData: ReviewFormData }) => {
      // Call the Supabase edge function to update the trial block using the SDK
      const { data, error } = await supabase.functions.invoke("submit-trial-block-review", {
        body: {
          id,
          // Pass the form data directly - the edge function will handle mapping
          ...formData,
        },
      })

      if (error) {
        // Surface the actual message from the edge function response body
        // (e.g. { "error": "invalid input syntax for type bigint: ..." })
        // rather than the generic "non-2xx status code" message.
        const message = await getSupabaseFunctionErrorMessage(error)
        throw new Error(message)
      }

      return data.data as TrialBlock
    },
    onSuccess: () => {
      // Invalidate and refetch the trial blocks query
      queryClient.invalidateQueries({ queryKey: ["trialBlocks"] })
    },
  })
}
